import {Observable, of, pipe, Subject} from 'rxjs';
import {filter, mergeMap} from 'rxjs/operators';
import {SecurityService} from '../../../components/security';
import {WebSocketMessage} from '../../../components/streaming/streaming-interfaces';
import {
    ArrayEncodedMdMessage,
    ArrayEncodedMdSnapshotMessage
} from '../../../models/common-ui-market-data/array-encoded-md-message';
import {DEFAULT_AGGREGATION_SCALE} from './aggregation';
import {MdSubscriptionSubject, SecurityMarketData} from './md-subscription-subject';
import {normalizeVwapSizes, vwapSizeToStr} from './vwap-utils';

enum VwapMdWebSocketMessageType {
    VWAP_MD_SNAPSHOT = 'VWAP_MD_SNAPSHOT'
}

interface VwapMdWebSocketMessage extends WebSocketMessage {
    messageType: VwapMdWebSocketMessageType.VWAP_MD_SNAPSHOT;
    data: ArrayEncodedMdMessage[];
}

export class VwapMdSubscriptionSubject extends MdSubscriptionSubject {

    public static getSubscriptionDictKey(vwapSizes: number[] | undefined): string {
        const normalizedVwapSizes = normalizeVwapSizes(vwapSizes);

        return normalizedVwapSizes && normalizedVwapSizes.length
            ? normalizedVwapSizes.join(',')
            : 'auto';
    }

    private static readonly sharedActualizeUnsubscription$ = new Subject<void>();

    public messageType: string;

    protected readonly isReadyToCreateSubjectType: boolean;

    constructor(
        protected vwapSizes: number[] | undefined,
        protected override securityService: SecurityService
    ) {
        super(DEFAULT_AGGREGATION_SCALE, securityService);

        // Skip early calling createSubjectType from super class
        this.isReadyToCreateSubjectType = true;

        this.createSubjectType();
    }

    protected override createSubjectType(): void {

        // Skip early calling from super class
        if (this.isReadyToCreateSubjectType) {
            const normalizedVwapSizes = normalizeVwapSizes(this.vwapSizes);
            const vwapSuffix = normalizedVwapSizes ? `#${normalizedVwapSizes.map(vwapSizeToStr).join(',')}` : '#auto';
            const messageType = `${VwapMdWebSocketMessageType.VWAP_MD_SNAPSHOT}${vwapSuffix}`;

            this.messageType = messageType;

            this.type = {
                name: `VWAP_MD${vwapSuffix}`,
                isMultiSubject: true,
                operations:                     {
                    SUBSCRIBE: `SUBSCRIBE_VWAP_MD${vwapSuffix}`,
                    UNSUBSCRIBE: `UNSUBSCRIBE_VWAP_MD${vwapSuffix}`
                },
                messagePipe:                    pipe(
                    filter((webSocketMessage: WebSocketMessage) => webSocketMessage.messageType === messageType),
                    mergeMap((mdWebSocketMessage: VwapMdWebSocketMessage): Observable<SecurityMarketData> => {
                        const mdMessageContainers = this.readMdSnapshot(
                            mdWebSocketMessage.data as ArrayEncodedMdSnapshotMessage[]
                        );

                        return of(...mdMessageContainers);
                    })
                ),
                sharedActualizeUnsubscription$: VwapMdSubscriptionSubject.sharedActualizeUnsubscription$,
                subjectPipeFactory: subject => this.createSubjectPipe(subject),
                deprecateSubject: subject => this.deprecateSubject(subject)
            };
        }
    }

}
